import { useMemo } from "react";
import { format, isToday, isTomorrow, startOfDay } from "date-fns";
import { he } from "date-fns/locale";
import styles from "../../styles/_containers/_appointments/calendarSidebar.module.scss";
import Loader from "../../components/loader";

// ── Upcoming appointments sidebar ──
const CalendarSidebar = ({
  appointments,    // ALL appointments array
  loading,
  employeeColors = {},
  isOpen,
  onToggle,
  onSelectAppt,   // callback(rawAppt) when user clicks a row
}) => {
  // Build sorted upcoming list from all appointments
  const upcoming = useMemo(() => {
    if (!appointments) return [];
    const now = new Date();
    const todayStart = startOfDay(now);

    return appointments
      .filter((appt) => {
        // Keep appointments from today onwards
        const apptDate = startOfDay(new Date(appt.date));
        return apptDate >= todayStart;
      })
      .map((appt) => {
        // Build combined datetime for sorting
        const d = new Date(appt.date);
        const [h, m] = (appt.timeSlot || "00:00").split(":").map(Number);
        d.setHours(h, m, 0, 0);
        return { ...appt, _sortDate: d };
      })
      .sort((a, b) => a._sortDate - b._sortDate)
      .slice(0, 30); // show up to 30 upcoming
  }, [appointments]);

  const labelForDate = (dateStr) => {
    const d = new Date(dateStr);
    if (isToday(d)) return "היום";
    if (isTomorrow(d)) return "מחר";
    return format(d, "EEEE, d בMMM", { locale: he });
  };

  // Group upcoming by date string
  const grouped = useMemo(() => {
    const groups = [];
    let lastDate = null;
    for (const appt of upcoming) {
      const dateKey = format(new Date(appt.date), "yyyy-MM-dd");
      if (dateKey !== lastDate) {
        groups.push({ date: dateKey, label: labelForDate(appt.date), items: [appt] });
        lastDate = dateKey;
      } else {
        groups[groups.length - 1].items.push(appt);
      }
    }
    return groups;
  }, [upcoming]);

  // ── Today's classes ──
  const todayClasses = useMemo(() => {
    if (!appointments) return [];
    const today = startOfDay(new Date());
    return appointments
      .filter((appt) => {
        const apptDate = startOfDay(new Date(appt.date));
        return (
          apptDate.getTime() === today.getTime() &&
          appt.serviceId?.type === "CLASS"
        );
      })
      .map((appt) => {
        const d = new Date(appt.date);
        const [h, m] = (appt.timeSlot || "00:00").split(":").map(Number);
        d.setHours(h, m, 0, 0);
        return { ...appt, _sortDate: d };
      })
      .sort((a, b) => a._sortDate - b._sortDate);
  }, [appointments]);

  return (
    <aside className={`${styles.sidebar} ${isOpen ? styles.sidebarOpen : styles.sidebarClosed}`}>
      {/* ── Toggle button ── */}
      {/* Sidebar is on the LEFT: when open → arrow points left (‹) to collapse; when closed → arrow points right (›) to expand */}
      <button
        type="button"
        className={styles.toggleBtn}
        onClick={onToggle}
        title={isOpen ? "מזעור" : "הרחב"}
        aria-label={isOpen ? "מזעור רשימת התורים" : "הצגת רשימת התורים"}
        aria-expanded={isOpen}
      >
        <svg className={styles.toggleIcon} viewBox="0 0 20 20" fill="none" aria-hidden="true">
          <path d={isOpen ? "m12.5 5-5 5 5 5" : "m7.5 5 5 5-5 5"} stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {/* ── Content (only when open) ── */}
      {isOpen && (
        <div className={styles.content}>
          {/* ── Today's classes ── */}
          {todayClasses.length > 0 && (
            <div className={styles.classSection}>
              <div className={styles.classSectionHeader}>
                <span className={styles.classSectionTitle}>שיעורים היום</span>
                <span className={styles.classSectionCount}>{todayClasses.length}</span>
              </div>
              {todayClasses.map((appt) => (
                <ClassRow
                  key={appt._id}
                  appt={appt}
                  color={appt.serviceId?.color || employeeColors?.[appt.employeeId] || "#DE1468"}
                  onSelect={onSelectAppt}
                />
              ))}
            </div>
          )}

          <div className={styles.header}>
            <div>
              <span className={styles.eyebrow}>המשך היום והשבוע</span>
              <span className={styles.headerTitle}>תורים קרובים</span>
            </div>
            {upcoming.length > 0 && (
              <span className={styles.headerCount}>{upcoming.length}</span>
            )}
          </div>

          {loading ? (
            <div className={styles.loaderWrap}><Loader /></div>
          ) : grouped.length === 0 ? (
            <div className={styles.emptyState}>
              <span className={styles.emptyIcon} aria-hidden="true">
                <svg viewBox="0 0 24 24" fill="none"><path d="M7 3v3M17 3v3M4 9h16" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" /><rect x="3.5" y="4.5" width="17" height="16" rx="3" stroke="currentColor" strokeWidth="1.7" /><path d="m9 15 2 2 4-4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
              <strong>היומן פנוי כרגע</strong>
              <p>תורים עתידיים יופיעו כאן לגישה מהירה</p>
            </div>
          ) : (
            <div className={styles.list}>
              {grouped.map((group) => (
                <div key={group.date} className={styles.group}>
                  <div className={styles.groupLabel}>{group.label}</div>
                  {group.items.map((appt) => (
                    <ApptRow
                      key={appt._id}
                      appt={appt}
                      color={employeeColors?.[appt.employeeId] || "#DE1468"}
                      onSelect={onSelectAppt}
                    />
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </aside>
  );
};

export default CalendarSidebar;

// ── Single class row ──
const ClassRow = ({ appt, color, onSelect }) => {
  const name = appt.serviceId?.name || "שיעור";
  const time = appt.timeSlot || "";
  const max = appt.serviceId?.maximumQueue || 1;
  const current = appt.numberOfAttendants || 0;
  const ratio = max > 0 ? current / max : 0;

  // Color coding: green (available), amber (≥80%), red (full)
  let capacityColor = "#22c55e";
  if (ratio >= 1) capacityColor = "#ef4444";
  else if (ratio >= 0.8) capacityColor = "#f59e0b";

  const isPastClass = (() => {
    const now = new Date();
    const d = new Date(appt.date);
    const [h, m] = (time || "00:00").split(":").map(Number);
    d.setHours(h, m, 0, 0);
    return d < now;
  })();

  return (
    <button
      type="button"
      className={`${styles.classRow} ${isPastClass ? styles.classRowPast : ""}`}
      onClick={() => onSelect(appt)}
      aria-label={`${name}, ${time}, ${current} מתוך ${max} משתתפים`}
    >
      <div className={styles.classAccent} style={{ backgroundColor: isPastClass ? "#cbd5e1" : color }} />
      <div className={styles.classInfo}>
        <div className={styles.classTop}>
          <span className={styles.classTime}>{time}</span>
          <span className={styles.className}>{name}</span>
        </div>
        <div className={styles.classBottom}>
          <span
            className={styles.classCapacity}
            style={{ color: isPastClass ? "#94a3b8" : capacityColor }}
          >
            {current}/{max}
          </span>
          <span className={styles.classCapacityLabel}>
            {ratio >= 1 ? "מלא" : ratio >= 0.8 ? "כמעט מלא" : "מקומות פנויים"}
          </span>
        </div>
      </div>
    </button>
  );
};

// ── Single appointment row ──
const ApptRow = ({ appt, color, onSelect }) => {
  const name    = appt.userId?.name    || "לקוח";
  const service = appt.serviceId?.name || "";
  const time    = appt.timeSlot        || "";

  // Check if appointment time has already passed
  const isPastAppt = (() => {
    const now = new Date();
    const apptDate = new Date(appt.date);
    const [h, m] = (time || "00:00").split(":").map(Number);
    apptDate.setHours(h, m, 0, 0);
    return apptDate < now;
  })();

  return (
    <button
      type="button"
      className={`${styles.apptRow} ${isPastAppt ? styles.apptRowPast : ""}`}
      onClick={() => onSelect(appt)}
      aria-label={`תור של ${name} בשעה ${time}${service ? `, ${service}` : ""}`}
    >
      <div
        className={styles.apptAccent}
        style={{ backgroundColor: isPastAppt ? "#cbd5e1" : color }}
      />
      <div className={styles.apptInfo}>
        <div className={styles.apptTop}>
          <span className={`${styles.apptTime} ${isPastAppt ? styles.apptTimePast : ""}`}>
            {time}
          </span>
          <span className={`${styles.apptName} ${isPastAppt ? styles.apptNamePast : ""}`}>
            {name}
          </span>
        </div>
        {service && (
          <span className={`${styles.apptService} ${isPastAppt ? styles.apptServicePast : ""}`}>
            {service}
          </span>
        )}
      </div>
    </button>
  );
};
